'use client';

import { type HTMLAttributes, type SVGProps, useCallback, useEffect, useState } from 'react';
import { BIRD_SVG_BODY, BIRD_VIEWBOX } from './bird-svg.js';
import { type MascotState, canTransition, durationMs } from './states.js';

export interface PerchBirdProps extends Omit<SVGProps<SVGSVGElement>, 'ref' | 'state'> {
  /** Current FSM state. Defaults to `idle`. */
  state?: MascotState;
  /** Rendered pixel size (bird is square). Defaults to 72. */
  size?: number;
  /** Accessible label for assistive tech. */
  label?: string;
  /** Optional hook called after every state transition. */
  onStateChange?: (to: MascotState, from: MascotState, ms: number) => void;
}

/**
 * React bird. Pure SVG + CSS — no WebGL, no canvas, no Lottie.
 * All animation is driven by the `data-state` attribute + CSS in `@perch/mascot/css`.
 */
export function PerchBird({
  state = 'idle',
  size = 72,
  label = 'Perch the bird',
  onStateChange,
  ...rest
}: PerchBirdProps) {
  const [active, setActive] = useState<MascotState>(state);

  useEffect(() => {
    if (active === state) {
      return;
    }
    if (!canTransition(active, state)) {
      // Route through idle — this preserves FSM safety.
      setActive('idle');
      return;
    }
    const ms = durationMs(active, state);
    onStateChange?.(state, active, ms);
    setActive(state);
  }, [state, active, onStateChange]);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={BIRD_VIEWBOX}
      width={size}
      height={size}
      className="perch-bird"
      data-state={active}
      role="img"
      aria-label={label}
      dangerouslySetInnerHTML={{ __html: BIRD_SVG_BODY }}
      {...rest}
    />
  );
}

export interface UsePerchBirdReturn {
  readonly state: MascotState;
  readonly setState: (next: MascotState) => void;
  readonly bind: { state: MascotState };
}

/**
 * Small hook that exposes a controlled FSM + props for `<PerchBird {...bind} />`.
 */
export function usePerchBird(initial: MascotState = 'idle'): UsePerchBirdReturn {
  const [state, setState] = useState<MascotState>(initial);

  const safeSet = useCallback((next: MascotState) => {
    setState((prev) => (canTransition(prev, next) ? next : 'idle'));
  }, []);

  return {
    state,
    setState: safeSet,
    bind: { state },
  };
}

export type { MascotState } from './states.js';
export { FriendlyBird } from './friendly-bird.js';
export type { FriendlyBirdProps } from './friendly-bird.js';
export { BIRD_PALETTES, ACCESSORIES } from './palettes.js';
export type { Accessory, BirdPalette, PaletteId } from './palettes.js';

export interface PerchBirdContainerProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
}
